import { Preloader } from '@hts/entities/Preloader';
import { mainActions, useAppDispatch, useAppSelector } from '@hts/shared/libs';
import { CardService } from '@hts/shared/services';
import { ICategoryItemInput } from '@hts/shared/types';
import { splitArrByComma } from '@hts/shared/utils';
import * as Sentry from '@sentry/react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ICardListItemState, ICardSize, ICategoryListFilters } from '../../types';
import { CardList } from './CardList';
import { CategoryFilters } from './CategoryFilters';
import { CategoryTags } from './CategoryTags';
import { CategoryTitle } from './CategoryTitle';
import { ViewHolder } from './ViewHolder';

import '../../styles/CategoryOfCard/CategoryList.scss';

interface ICategoryListProps {
  category: string;
  subCategory: string | null;
}

export const CategoryList: React.FC<ICategoryListProps> = memo(({ category, subCategory }) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);

  const allCategories = useAppSelector((state) => state.mainReducer.allCategories);

  const cardService = useMemo(() => new CardService(), []);

  const [loading, setLoading] = useState(true);
  const [cardsList, setCardsList] = useState<ICardListItemState[]>([]);
  const [bestSellersList, setBestSellersList] = useState<any[]>([]);
  const [cardWidthClass, setCardWidthClass] = useState('fourth-width');
  const [tags, setTags] = useState<any[]>([]);
  const [sizes, setSizes] = useState<ICardSize[]>([]);
  const [languages, setLanguages] = useState<any[]>([]);
  const [filters, setFilters] = useState<ICategoryListFilters>({
    tags: [],
    size: [],
    language: [],
    price: [],
    sub: [],
  });
  const [currentActiveCategory, setCurrentActiveCategory] = useState<ICategoryItemInput | null>(
    null
  );
  const [currentActiveSubcategory, setCurrentActiveSubcategory] = useState<any | null>(null);

  const priceSort = useMemo(() => ['high to low', 'low to high'], []);

  const toggleActiveCategory = useCallback((set: boolean, cat: any = null, sub: any = null) => {
    if (set) {
      dispatch(mainActions.setActiveCategory(cat));
      dispatch(mainActions.setActiveSubcategory(sub));
    } else {
      dispatch(mainActions.unsetActiveCategory());
      dispatch(mainActions.unsetActiveSubcategory());
    }
  }, []);

  const findCategoryData = useCallback(
    (inputCategory: string) => {
      let result: ICategoryItemInput | null = null;

      for (const category of allCategories) {
        if (category.slug === inputCategory) {
          result = category;
          break;
        }

        const sub = category.categories?.find((subcat) => subcat.slug === inputCategory);

        if (sub) {
          result = sub;
          break;
        }
      }

      return result;
    },
    [allCategories]
  );

  const findSubCategoryData = useCallback(
    (categoryData: ICategoryItemInput | undefined, subcategory: string | null) =>
      subcategory ? categoryData?.categories?.find((cat) => cat.slug === subcategory) : null,
    []
  );

  const setCardsListState = useCallback((newArr: ICardListItemState[]) => {
    const newLanguages: string[] = [];
    const newSizes: string[] = [];

    newArr.forEach(({ cardDetails }) => {
      if (cardDetails) {
        if (!newSizes.includes(cardDetails.size)) {
          newSizes.push(cardDetails.size);
        }

        if (!newLanguages.includes(cardDetails.language)) {
          newLanguages.push(cardDetails.language);
        }
      }
    });

    const newSizesState = setSizesNamesState(newSizes);

    setCardsList((prev) => [...prev, ...newArr]);
    setSizes(newSizesState);
    setLanguages(newLanguages);
    setLoading(false);
  }, []);

  const setSizesNamesState = useCallback((sizes: string[]) => {
    const newSizes: ICardSize[] = [];

    sizes.forEach((item) => {
      let sizeName = '';

      switch (item) {
        case '8.5 x 11':
          sizeName = 'Portrait';
          break;
        case '8.5 x 8.5':
          sizeName = 'Square';
          break;
        case '11 x 8.5':
          sizeName = 'Landscape';
          break;
      }

      const pushItem: ICardSize = { size: item, name: sizeName };

      if (pushItem.name) {
        newSizes.push(pushItem);
      }
    });

    return newSizes;
  }, []);

  const getCategoryList = useCallback(
    async (inputCategory: string, inputSubCategory: string | null) => {
      setLoading(true);
      setCardsList([]);

      const sortedCategoriesIds: number[] = [];

      try {
        const result = await cardService.getSubmenuCategories({
          signal: abortControllerRef.current?.signal,
        });
        const slugCategories = result.categories.find((cat) => cat.slug === inputCategory);

        if (slugCategories) {
          const ids = slugCategories.children.map((cat) => cat.id);
          sortedCategoriesIds.push(...ids);
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        Sentry.captureException(err, {
          tags: { source: 'getCategoryList' },
          extra: { inputCategory, inputSubCategory },
        });
        console.log('Error getSubmenuCategories', err);
      }

      const categoryData = findCategoryData(inputCategory);

      if (!categoryData) {
        setLoading(false);
        return;
      }

      const categoryDataChildren = categoryData.categories ? [...categoryData.categories] : [];
      const cleanCategoryData: ICategoryItemInput = {
        ...categoryData,
        categories: categoryDataChildren,
      };

      const orderMap = sortedCategoriesIds.reduce<{ [id: number]: number }>((acc, id, index) => {
        acc[id] = index;
        return acc;
      }, {});

      const sortedCategoriesEfficient = categoryDataChildren.sort(
        (a, b) => orderMap[a.id] - orderMap[b.id]
      );

      cleanCategoryData.categories = sortedCategoriesEfficient;

      const subcategoryData = findSubCategoryData(cleanCategoryData, inputSubCategory);
      const id = inputSubCategory ? subcategoryData?.id : categoryData?.id;

      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const categoryCards = await cardService.getCardsByCategoryId(id, {
          signal: abortControllerRef.current?.signal,
        });

        const updatedCategoryCards: ICardListItemState[] = categoryCards.map((elem, i) => ({
          ...elem,
          index: i,
        }));

        const tags = subcategoryData ? subcategoryData.tags : cleanCategoryData?.tags || [];

        setCardsListState(updatedCategoryCards);
        toggleActiveCategory(true, cleanCategoryData, subcategoryData);

        setTags(tags);
        setLoading(false);
        setCurrentActiveCategory(cleanCategoryData);
        setCurrentActiveSubcategory(subcategoryData?.name || null);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          console.log('Request was aborted');
          return;
        }

        Sentry.captureException(err);
        console.log('Error', err);
        setLoading(false);
      }
    },
    [findCategoryData, findSubCategoryData, setCardsListState, toggleActiveCategory]
  );

  const getQueryParams = useCallback(() => {
    const tags = splitArrByComma(searchParams.get('tags'));
    const size = splitArrByComma(searchParams.get('size'));
    const language = splitArrByComma(searchParams.get('language'));
    const price = splitArrByComma(searchParams.get('price'));
    const sub = splitArrByComma(searchParams.get('sub'));

    setFilters({ tags, size, language, price, sub });
  }, [searchParams]);

  const dropdownOpenerListener = useCallback((e) => {
    e.preventDefault();

    const activeClass = 'custom-dropdown-active';
    const holder = e.target.parentElement;

    holder.classList.toggle(activeClass);
  }, []);

  const initDropdown = useCallback(
    (flag?: 'refresh') => {
      const dropdownList = document.querySelectorAll('.custom-dropdown');

      dropdownList.forEach((holder) => {
        const opener = holder.querySelector('.custom-dropdown-opener');
        const drop = holder.querySelector('.custom-dropdown-drop');
        const dropLinks = drop?.querySelectorAll('a');
        const activeClass = 'custom-dropdown-active';

        if (flag === 'refresh') {
          opener?.removeEventListener('click', dropdownOpenerListener);
        }
        opener?.addEventListener('click', dropdownOpenerListener);

        dropLinks?.forEach((link) => {
          if (flag === 'refresh') {
            link.removeEventListener('click', clickFunction);
          }

          link.addEventListener('click', clickFunction);
        });

        if (flag === 'refresh') {
          document.removeEventListener('click', outSideClickFunction);
        }

        document.addEventListener('click', outSideClickFunction);

        function clickFunction(e) {
          e.preventDefault();
          holder.classList.remove(activeClass);
        }

        function outSideClickFunction(event) {
          const isClickInside = holder.contains(event.target);

          if (!isClickInside) {
            holder.classList.remove(activeClass);
          }
        }
      });
    },
    [dropdownOpenerListener]
  );

  const setCardWidth = useCallback((e, width) => {
    e.preventDefault();
    setCardWidthClass(width);
  }, []);

  useEffect(() => {
    getQueryParams();
    // initDropdown();

    // const timerID = setInterval(() => {
    //   setTimer((prevTimer) => prevTimer + 1);
    // }, 1000);

    const windowWidth = window.innerWidth;

    if (windowWidth < 768) {
      setCardWidthClass('two-width');
    }

    return () => {
      const dropOpeners = document.querySelectorAll('.custom-dropdown .custom-dropdown-opener');

      dropOpeners.forEach((item) => {
        item.removeEventListener('click', dropdownOpenerListener);
      });

      // clearInterval(timerID);
      toggleActiveCategory(false);
    };
  }, [getQueryParams]);

  useEffect(() => {
    if (allCategories.length) {
      abortControllerRef.current = new AbortController();
      getCategoryList(category, subCategory);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [allCategories.length, category, getCategoryList, subCategory]);

  useEffect(() => {
    if (!loading) {
      initDropdown();
    }

    return () => {
      initDropdown('refresh');
    };
  }, [initDropdown, loading]);

  const toggleFilters = useCallback(
    async (name: string, val: string) => {
      const currentFilters = { ...filters };
      const params = new URLSearchParams(window.location.search);

      let all = false;

      if (val === 'all') {
        all = true;
      }

      switch (name) {
        case 'tags':
          params.delete('tags');
          if (currentFilters[name].includes(val)) {
            currentFilters[name] = currentFilters[name].filter((item) => item !== val);
          } else {
            currentFilters[name].push(val);
          }
          if (currentFilters[name].length > 0) {
            params.append('tags', currentFilters[name].join(','));
          }
          break;

        case 'size':
          params.delete('size');
          if (currentFilters[name].includes(val) || all) {
            currentFilters[name] = [];
          } else {
            currentFilters[name] = [val];
            params.append('size', currentFilters[name].join(','));
          }
          break;

        case 'language':
          params.delete('language');
          if (currentFilters[name].includes(val) || all) {
            currentFilters[name] = [];
          } else {
            currentFilters[name] = [val];
            params.append('language', currentFilters[name].join(','));
          }
          break;

        case 'sub':
          params.delete('tags');
          currentFilters['tags'] = [];

          params.delete('size');
          currentFilters['size'] = [];

          params.delete('language');
          currentFilters['language'] = [];

          params.delete('price');
          currentFilters['price'] = [];

          currentFilters['sub'] = [val];

          params.delete('sub');
          params.append('sub', val);

          setCurrentActiveSubcategory(val);

          break;

        case 'price':
          params.delete('price');
          if (currentFilters[name].includes(val) || all) {
            currentFilters[name] = [];
          } else {
            currentFilters[name] = [val];
            params.append('price', currentFilters[name].join(','));
          }
          break;

        default:
          currentFilters[name] = [val];
      }

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      navigate(newUrl, { replace: true });

      setFilters(currentFilters);
    },
    [filters, allCategories.length, category, getCategoryList, navigate]
  );

  if (loading) {
    return <Preloader />;
  }

  return (
    <section className="listing-section">
      <div className="container">
        {currentActiveCategory && (
          <div className="category-heading">
            <CategoryTitle
              currentActiveCategory={currentActiveCategory}
              currentActiveSubcategory={currentActiveSubcategory}
              toggleFilters={toggleFilters}
            />

            <CategoryFilters
              filters={filters}
              sizes={sizes}
              languages={languages}
              priceSort={priceSort}
              toggleFilters={toggleFilters}
            />

            <CategoryTags tags={tags} filters={filters} toggleFilters={toggleFilters} />

            <ViewHolder cardWidthClass={cardWidthClass} setCardWidth={setCardWidth} />
          </div>
        )}

        <div className="listing-holder">
          <div className={`listing ${cardWidthClass}`}>
            <CardList list={cardsList} filters={filters} />
          </div>
        </div>

        {/* <BestSellers cardList={bestSellersList} /> */}
      </div>
    </section>
  );
});

export default CategoryList;
